// Import algoliasearch from node_modules instead of using CDN
// Only import algoliasearch if the search block is present on the page
import algoliasearch from "./algoliasearch-lite.esm.browser";

import { __, _x } from "@wordpress/i18n"; // Import __ for translation

/**
 * Debounce function that returns a Promise
 * @param {Function} fn - Function to debounce
 * @param {number} time - Debounce delay in milliseconds
 * @returns {Function} Debounced function that returns a Promise
 */
function debouncePromise(fn, time) {
	let timer = undefined;
	return function debounced(...args) {
		if (timer) {
			clearTimeout(timer);
		}
		return new Promise((resolve) => {
			timer = setTimeout(() => resolve(fn(...args)), time);
		});
	};
}

/**
 * Translate provider type from Dutch to the current locale
 * @param {string} providerType - The Dutch provider type from Algolia
 * @param {Object} translations - Translation object from PHP
 * @returns {string} Translated provider type
 */
function translateProviderType(providerType, translations) {
	// If we have specific translations for this provider type, use them
	if (
		translations &&
		translations.providerTypes &&
		translations.providerTypes[providerType]
	) {
		return translations.providerTypes[providerType];
	}

	// Fallback: return the original provider type
	return providerType;
}

/**
 * Check if wp-search-with-algolia plugin is properly configured
 * @param {Object} bruidAlgolia - The bruidAlgolia configuration object
 * @returns {boolean} True if properly configured
 */
function isAlgoliaPluginConfigured(bruidAlgolia) {
	// Only proceed if an index is explicitly selected (no fallback)
	return (
		bruidAlgolia &&
		bruidAlgolia.appId &&
		bruidAlgolia.searchKey &&
		bruidAlgolia.exactIndex &&
		bruidAlgolia.exactIndex.trim() !== "" &&
		bruidAlgolia.pluginSettings
	);
}

document.addEventListener("DOMContentLoaded", function () {
	// Ensure all dependencies are loaded
	setTimeout(function () {
		// Make sure bruidAlgolia exists - if it doesn't, check if we should warn
		if (typeof window.bruidAlgolia === "undefined") {
			return;
		}

		// Store a local reference for convenience
		const bruidAlgolia = window.bruidAlgolia;

		// Check if wp-search-with-algolia plugin is properly configured
		if (!isAlgoliaPluginConfigured(bruidAlgolia)) {
			// Don't initialize - fail silently
			return;
		}

		// Configuration options with defaults
		const config = {
			enablePagination: false, // Disabled by default
			enableFacets: false, // Disabled by default
			debounceDelay: bruidAlgolia.pluginSettings?.autocompleteDebounce || 200, // Use plugin's debounce rate
			showInitialResults: true, // Default to true for backward compatibility
			postsPerPage: 12, // Default posts per page
			resultDisplayType: "cards", // Default to custom cards
			postTypeFilter: "", // Default to no post type filter
			...bruidAlgolia.config, // Override with any config from PHP
		};

		// Handle different display types
		if (config.resultDisplayType === "autocomplete") {
			// Use wp-search-with-algolia autocomplete
			initializeAutocomplete();
			return; // Exit early, don't initialize custom cards
		} else {
			// Ensure any existing autocomplete instances are disabled when using cards mode
			disableExistingAutocomplete();
		}

		// Find our container elements using consistent IDs
		const searchInput = document.getElementById("algolia-search-input");
		const hitsContainer = document.getElementById("algolia-search-hits");
		const paginationContainer = document.getElementById(
			"algolia-search-pagination",
		);
		const filtersContainer = document.getElementById("algolia-search-filters");

		// Hide pagination container if pagination is disabled
		if (!config.enablePagination && paginationContainer) {
			paginationContainer.style.display = "none";
		}

		if (!searchInput || !hitsContainer) {
			return;
		}

		try {
			// Initialize the Algolia client using the imported module
			const searchClient = algoliasearch(
				bruidAlgolia.appId,
				bruidAlgolia.searchKey,
			);

			// Create a direct search function
			let lastQuery = "";
			let currentPage = 0;
			let activeFilters = new Set(); // Store active taxonomy filters
			let allSchemaSections = []; // Store all available schema_section values

			// Function to fetch all schema_section/type values from Algolia using facets
			async function fetchAllSchemaSections() {
				try {
					const index = searchClient.initIndex(bruidAlgolia.exactIndex);

					// Determine which field to use for facets based on content type
					const facetField =
						bruidAlgolia.contentType === "aanbieder"
							? "type"
							: "taxonomies.schema_article_section"; // Use the correct nested field for posts

					// Build search params based on content type
					const searchParams = {
						facets: [facetField],
						hitsPerPage: 0,
					};

					// For aanbieder, add country filter only if not "all"
					if (bruidAlgolia.contentType === "aanbieder") {
						if (config.country && config.country !== "all") {
							searchParams.filters = `country:"${config.country}"`;
						}
					}

					const facetResponse = await index.search("", searchParams);

					const facetTypes =
						facetResponse.facets && facetResponse.facets[facetField]
							? Object.keys(facetResponse.facets[facetField])
							: [];

					return facetTypes.sort();
				} catch (e) {
					return [];
				}
			}

			// Function to render filter tags
			function renderFilterTags(availableTaxonomies = []) {
				if (!filtersContainer) {
					// Silently return if filters are disabled and container doesn't exist
					return;
				}

				// Clear existing filters
				filtersContainer.innerHTML = "";

				if (!availableTaxonomies || availableTaxonomies.length === 0) return;

				// Create filter title if we have taxonomies to show
				const filterTitle = document.createElement("div");
				// filterTitle.className = "mb-3 font-semibold text-gray-700";
				// filterTitle.textContent =
				// 	bruidAlgolia.translations?.filterByCategory || "Filter op categorie:";
				filtersContainer.appendChild(filterTitle);

				// Create filter tags container
				const tagsContainer = document.createElement("div");
				tagsContainer.className = "flex flex-wrap justify-center gap-2 mb-4";

				// Add filter tags
				availableTaxonomies.forEach((taxonomy) => {
					const tag = document.createElement("button");
					const isActive = activeFilters.has(taxonomy);
					const anyFilterActive = activeFilters.size > 0;

					// If any filter is active, gray out non-selected filters
					// If no filter is active, all filters have the default style
					tag.className = isActive
						? "px-3 py-1 text-sm bg-primary-500 text-white rounded-md transition-colors hover:bg-primary-600"
						: anyFilterActive
							? "px-3 py-1 text-sm bg-gray-100 text-gray-400 rounded-md transition-colors hover:bg-gray-200 hover:text-gray-600"
							: "px-3 py-1 text-sm bg-gray-200 rounded-md transition-colors hover:bg-gray-300";

					// Use translated label for display (only for aanbieder), otherwise use raw value
					const displayText =
						bruidAlgolia.contentType === "aanbieder"
							? translateProviderType(taxonomy, bruidAlgolia.translations)
							: taxonomy; // For posts, show the schema_section value as-is

					tag.textContent = displayText;

					tag.addEventListener("click", () => {
						// Allow only one filter at a time
						if (isActive) {
							// If clicking on the active filter, deselect it
							activeFilters.clear();
						} else {
							// Clear previous selections and set the new one
							activeFilters.clear();
							activeFilters.add(taxonomy);
						}

						// Re-render filters
						renderFilterTags(availableTaxonomies);

						// Re-run search with filters (always trigger search, even if query is empty)
						performSearch(lastQuery, 0);
					});

					tagsContainer.appendChild(tag);
				});

				// Add clear filters button if there are active filters
				if (activeFilters.size > 0) {
					const clearButton = document.createElement("button");
					clearButton.className =
						"bg-weddingred px-3 py-1 text-sm text-white rounded-md transition-colors hover:text-black hover:bg-gray-200";
					clearButton.innerHTML = `
						<svg xmlns="http://www.w3.org/2000/svg" class="mx-auto w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
						</svg>`;

					clearButton.addEventListener("click", () => {
						activeFilters.clear();
						renderFilterTags(availableTaxonomies);

						// Always trigger search, even if query is empty
						performSearch(lastQuery, 0);
					});

					tagsContainer.appendChild(clearButton);
				}

				filtersContainer.appendChild(tagsContainer);
			}

			// Function to scroll to results with offset
			function scrollToResults() {
				// Find the search block container or use the search input as fallback
				let scrollTarget = searchInput;

				// Try to find the parent block container (typically has a class like 'algolia-search-block-v2')
				const blockContainer = hitsContainer.closest(
					".algolia-search-block-v2",
				);
				if (blockContainer) {
					scrollTarget = blockContainer;
				}

				// Get the target element position
				const rect = scrollTarget.getBoundingClientRect();
				const scrollTop =
					window.pageYOffset || document.documentElement.scrollTop;

				// Calculate offset (adjust this value based on your header height)
				// 120px accounts for typical fixed headers, adjust as needed
				const offset = 120;
				const targetPosition = rect.top + scrollTop - offset;

				// Smooth scroll to position
				window.scrollTo({
					top: targetPosition,
					behavior: "smooth",
				});
			}

			// Function to perform search
			function performSearch(query, page = 0) {
				// Show loading state
				hitsContainer.innerHTML =
					'<div class="flex justify-center items-center py-12"><div class="loader"></div></div>';

				// Get the index and search
				const index = searchClient.initIndex(bruidAlgolia.exactIndex);

				// Configure search parameters based on content type
				const searchParams = {
					page: page,
					hitsPerPage: config.postsPerPage, // Use the configured posts per page
					highlightPreTag: '<span class="text-white bg-black">',
					highlightPostTag: "</span>",
				};

				// Add base post_type filter for post content type
				// NOTE: Only apply filter if post_type field exists in your index
				if (bruidAlgolia.contentType === "post") {
					// TEMPORARILY DISABLED for debugging - uncomment when post_type field is confirmed
					// searchParams.filters = "post_type:post";
				}

				// Add post type filter if specified (this would override the base filter)
				if (config.postTypeFilter && bruidAlgolia.contentType === "post") {
					// Try different common field names for post type
					const fieldNames = ["post_type", "type", "content_type"];
					let filterApplied = false;

					// Try each field name until one works
					for (const fieldName of fieldNames) {
						try {
							if (config.postTypeFilter === "custom") {
								searchParams.filters = `NOT ${fieldName}:post AND NOT ${fieldName}:page`;
							} else {
								searchParams.filters = `${fieldName}:${config.postTypeFilter}`;
							}
							filterApplied = true;
							break;
						} catch (e) {
							continue;
						}
					}

					// If no field worked, fall back to post_type
					if (!filterApplied) {
						if (config.postTypeFilter === "custom") {
							searchParams.filters =
								"NOT post_type:post AND NOT post_type:page";
						} else {
							searchParams.filters = `post_type:${config.postTypeFilter}`;
						}
					}
				}

				// Add specific filters for aanbieder/wedding guide content type
				if (bruidAlgolia.contentType === "aanbieder") {
					// Only add country filter if a specific country is selected (not "all")
					if (config.country && config.country !== "all") {
						searchParams.filters = `country:"${config.country}"`;
					}

					// Add optional filters for subscription level (boosts results by subscription)
					searchParams.optionalFilters = [
						'abbo:"Premium Plus"',
						'abbo:"Premium"',
						'abbo:"Basic"',
						'abbo:"Free"',
					];

					// Add TYPE filter if any are active (aanbieder uses "type" field)
					if (activeFilters.size > 0) {
						const typeFilters = Array.from(activeFilters)
							.map((type) => `type:"${type}"`)
							.join(" OR ");
						if (typeFilters) {
							if (searchParams.filters) {
								searchParams.filters += ` AND (${typeFilters})`;
							} else {
								searchParams.filters = typeFilters;
							}
						}
					}
				}
				// Add schema_article_section filters for post content type
				else if (
					bruidAlgolia.contentType === "post" &&
					activeFilters.size > 0
				) {
					// Use taxonomies.schema_article_section filter for posts
					const sectionFilters = Array.from(activeFilters)
						.map((section) => `taxonomies.schema_article_section:"${section}"`)
						.join(" OR ");

					if (sectionFilters) {
						// Combine post type filter with schema_section filters (if post_type filter is enabled)
						if (searchParams.filters) {
							searchParams.filters += ` AND (${sectionFilters})`;
						} else {
							searchParams.filters = sectionFilters;
						}
					}
				}

				index
					.search(query, searchParams)
					.then(function (response) {
						// Clear containers
						hitsContainer.innerHTML = "";

						// Extract available taxonomies for filtering
						// Only show filters if facets are enabled
						if (
							config.enableFacets &&
							(bruidAlgolia.contentType === "aanbieder" ||
								bruidAlgolia.contentType === "post")
						) {
							// Show all schema_section values as filters (from Algolia facets)
							renderFilterTags(allSchemaSections);
						}

						if (response.hits.length === 0) {
							hitsContainer.innerHTML = `
							${bruidAlgolia.contentType === "aanbieder"
									? `<div class="flex flex-col justify-center items-center px-4 py-12">
									<h3 class="mb-4 text-xl font-semibold text-gray-900">${bruidAlgolia.translations?.noVendorsFoundTitle ||
									"Geen aanbieders gevonden"
									}</h3>
									<p class="text-center text-gray-600">${bruidAlgolia.translations?.noVendorsFoundDescription ||
									"We konden geen aanbieders vinden die overeenkomen met je zoekopdracht. Probeer andere zoektermen."
									}</p>
								</div>`
									: `<div class="flex flex-col justify-center items-center px-4 py-12">
									<h3 class="mb-4 text-xl font-semibold text-gray-900">${bruidAlgolia.translations?.noResultsFoundTitle ||
									"Geen resultaten gevonden"
									}</h3>
									<p class="text-center text-gray-600">${bruidAlgolia.translations?.noResultsFoundDescription ||
									"We konden geen artikelen vinden die overeenkomen met je zoekopdracht. Probeer andere zoektermen."
									}</p>
								</div>`
								}
						`;
							return;
						}

						// Create results grid with Tailwind
						const resultsGrid = document.createElement("div");
						resultsGrid.className =
							"grid gap-4 lg:grid-cols-4 lg:gap-8 lg:pb-8 max-lg:relative max-lg:grid-flow-col max-lg:pb-8 max-lg:snap-x max-xl:overflow-x-auto";

						// Process hits - using different templates based on content type
						const contentType = bruidAlgolia.contentType;

						response.hits.forEach(function (hit) {
							// Create article card
							const article = document.createElement("article");
							article.className =
								"relative h-full flex flex-col max-lg:w-[75vw] max-lg:flex-shrink-0 max-lg:snap-start group";

							if (contentType === "aanbieder") {
								// Template for wedding guide entries/aanbieder
								// Get thumbnail image (300x300) from images object
								let imageUrl = "";

								// Primary source: images.thumbnail
								if (hit.images && hit.images.thumbnail && hit.images.thumbnail.url) {
									imageUrl = hit.images.thumbnail.url;
								}
								// Fallback to featured_image
								else if (hit.featured_image) {
									imageUrl = hit.featured_image;
								}
								// Fallback to image_urls
								else if (hit.image_urls && hit.image_urls.thumbnail) {
									imageUrl = hit.image_urls.thumbnail;
								} else if (
									hit.image_urls &&
									Object.keys(hit.image_urls).length > 0
								) {
									imageUrl = Object.values(hit.image_urls)[0];
								}
								// Fallback to photos
								else if (
									hit.photos &&
									hit.photos.Default &&
									hit.photos.Default.thumb
								) {
									imageUrl = hit.photos.Default.thumb;
								} else if (hit.photos) {
									const photos = Object.values(hit.photos);
									if (photos.length > 0 && photos[0] && photos[0].thumb) {
										imageUrl = photos[0].thumb;
									}
								}

								// Build image element
								const imageHtml = imageUrl
									? `<img
									src="${imageUrl}"
									alt="${hit.name || "Wedding vendor"}"
									class="aspect-square algolia-hit-image object-cover object-center"
									loading="lazy"
									data-has-fallback="true"
								/>`
									: `<div class="aspect-[4/3] w-full bg-gray-100 flex items-center justify-center rounded-2xl lg:h-full lg:mb-6">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-12 h-12 text-gray-300">
										<path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
									</svg>
								</div>`;

								// Get highlighted name
								let name = hit.name || "Unnamed";
								if (hit._highlightResult && hit._highlightResult.name) {
									name = hit._highlightResult.name.value;
								}
								https://www.dev.site/bruidsbeurzen/zuidwolde/narline-28862/https://www.dev.site/bruidsbeurzen/zuidwolde/narline-28862/
								article.innerHTML = `
								<div class="overflow-hidden relative rounded-2xl">
									${imageHtml}
								</div>
								<div class="-mt-8 w-[95%] mx-auto z-20 flex flex-col flex-1 p-6 bg-white rounded-2xl shadow-lg group-hover:shadow-sm transition-all duration-300">
									<h3 class="heading-6 mb-4 transition-colors group-hover:text-primary-500 hover:text-primary-500">
										${name}
									</h3>
									<p class="text-sm text-gray-600">
									${hit.zipcode || ""}, ${hit.city || ""}, ${hit.street || ""} ${hit.housenumber || ""
									}
									</p>
								</div>
								<a href="${hit.url || "#"}" title="${hit.name || "Wedding vendor"
									}" rel="noopener" class="block before:content-[''] before:absolute before:inset-0 z-20"></a>
							`;
							} else {
								// Get schema_article_section terms
								let schemaSectionsHtml = "";
								if (
									hit.schema_article_section &&
									Array.isArray(hit.schema_article_section)
								) {
									const sections = hit.schema_article_section.slice(0, 3);
									schemaSectionsHtml = sections
										.map(
											(section) =>
												`<span class="px-2 py-1 text-xs text-white bg-neutral-500 rounded-md">${section.name}</span>`,
										)
										.join("");
								}
								// Get image from images object
								let imageUrl = "";

								// Primary source: images.490 (490x380)
								if (hit.images && hit.images["490"] && hit.images["490"].url) {
									imageUrl = hit.images["490"].url;
								}
								// Fallback to images.thumbnail (300x300) only
								else if (hit.images && hit.images.thumbnail && hit.images.thumbnail.url) {
									imageUrl = hit.images.thumbnail.url;
								}
								// No other fallbacks - if 490 or thumbnail aren't available, show placeholder

								// Build image element
								const imageHtml = imageUrl
									? `<img
									src="${imageUrl}"
									alt="${hit.post_title || "Article"}"
									class="aspect-square algolia-hit-image object-cover object-center"
									loading="lazy"
									data-has-fallback="true"
								/>`
									: `<div class="aspect-square">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-12 h-12 text-gray-300">
										<path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
									</svg>
								</div>`;

								// Get highlighted title
								let title = hit.post_title || "Untitled";
								if (hit._highlightResult && hit._highlightResult.post_title) {
									title = hit._highlightResult.post_title.value;
								}

								// Set article HTML
								article.innerHTML = `
								<div class="overflow-hidden relative rounded-2xl">
									<div class="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
										${schemaSectionsHtml}
									</div>
									${imageHtml}
								</div>
								<div class="-mt-8 z-20 flex flex-col flex-1 p-4 mx-2 bg-white rounded-2xl shadow-lg transition-all duration-300 group-hover:shadow-sm">
									<h3 class="heading-6 mb-4 transition-colors duration-300 group-hover:text-primary-500">
										${title}
									</h3>
									<span class="is-style-with-arrow mt-auto inline-flex justify-end items-center gap-2 text-sm font-bold tracking-wide transition-colors group hover:text-primary-500 lg:text-primary-900 max-lg:text-tertiary-500 max-lg:font-bold">
										<span class="leading-none">${bruidAlgolia.translations?.readMoreLabel ||
									"Lees het hele artikel"
									}</span>
										<span class="size-6 relative transition-transform duration-100 ease-in-out group-hover:translate-x-1">
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
												<path fill-rule="evenodd" d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z" clip-rule="evenodd" />
											</svg>
										</span>
									</span>
								</div>
								<a href="${hit.permalink}" title="${hit.post_title || "Article"
									}" rel="noopener" class="block before:content-[''] before:absolute before:inset-0 z-20"></a>
							`;
							}

							resultsGrid.appendChild(article);
						});

						hitsContainer.appendChild(resultsGrid);

						// Handle image load errors - replace with placeholder
						const images = resultsGrid.querySelectorAll('img[data-has-fallback="true"]');
						images.forEach(img => {
							img.addEventListener('error', function () {
								// Create placeholder element
								const placeholder = document.createElement('div');
								placeholder.className = 'aspect-square bg-gray-100 flex items-center justify-center rounded-2xl';
								placeholder.innerHTML = `
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-12 h-12 text-gray-300">
										<path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
									</svg>
								`;
								// Replace image with placeholder
								this.parentElement.replaceChild(placeholder, this);
							}, { once: true }); // Only trigger once per image
						});

						// Only show pagination if it's enabled in configuration
						if (
							config.enablePagination &&
							response.nbPages > 1 &&
							paginationContainer
						) {
							paginationContainer.style.display = "flex";
							paginationContainer.innerHTML = "";

							if (response.page > 0) {
								const prevButton = document.createElement("button");
								prevButton.className =
									"px-4 py-2 bg-gray-200 rounded-md border-0 transition-colors hover:bg-gray-300";
								prevButton.textContent =
									bruidAlgolia.translations?.previousPageLabel || "Vorige";
								prevButton.addEventListener("click", function () {
									currentPage = response.page - 1;
									performSearch(lastQuery, currentPage);
									// Scroll to top of results with offset
									scrollToResults();
								});
								paginationContainer.appendChild(prevButton);
							}

							// Page numbers
							const startPage = Math.max(
								0,
								Math.min(response.page - 2, response.nbPages - 5),
							);
							const endPage = Math.min(startPage + 4, response.nbPages - 1);

							for (let i = startPage; i <= endPage; i++) {
								const pageButton = document.createElement("button");
								pageButton.className =
									i === response.page
										? "border-0 px-4 py-2 bg-black text-white rounded-md"
										: "border-0 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors";
								pageButton.textContent = i + 1;
								pageButton.addEventListener("click", function () {
									if (i !== response.page) {
										currentPage = i;
										performSearch(lastQuery, currentPage);
										// Scroll to top of results with offset
										scrollToResults();
									}
								});
								paginationContainer.appendChild(pageButton);
							}

							if (response.page < response.nbPages - 1) {
								const nextButton = document.createElement("button");
								nextButton.className =
									"px-4 py-2 bg-gray-200 rounded-md border-0 transition-colors hover:bg-gray-300";
								nextButton.textContent =
									bruidAlgolia.translations?.nextPageLabel || "Volgende";
								nextButton.addEventListener("click", function () {
									currentPage = response.page + 1;
									performSearch(lastQuery, currentPage);
									// Scroll to top of results with offset
									scrollToResults();
								});
								paginationContainer.appendChild(nextButton);
							}
						} else if (paginationContainer) {
							// Ensure pagination is hidden if disabled
							paginationContainer.style.display = "none";
						}
					})
					.catch(function (error) {
						hitsContainer.innerHTML = `
						<div class="flex flex-col justify-center items-center px-4 py-12">
							<h3 class="mb-4 text-xl font-semibold text-red-600">Error</h3>
							<p class="text-center text-gray-600">${error.message}</p>
						</div>
					`;
					});
			}

			// Create debounced search function with 200ms delay
			const debouncedSearch = debouncePromise(
				(query, page) => performSearch(query, page),
				config.debounceDelay,
			);

			// Add event listener to search input with debounce
			searchInput.addEventListener("keyup", function (e) {
				const query = e.target.value.trim();

				if (query.length > 2) {
					lastQuery = query;
					currentPage = 0;
					debouncedSearch(query, 0);
				} else if (query.length === 0) {
					// When input is cleared, reset to initial state showing initial results
					lastQuery = "";
					currentPage = 0;
					performSearch("", 0); // Show initial results instead of clearing
				}
			});

			// Initial search if there's an initial query or showInitialResults is true
			if (bruidAlgolia.searchQuery) {
				searchInput.value = bruidAlgolia.searchQuery;
				lastQuery = bruidAlgolia.searchQuery;
				performSearch(bruidAlgolia.searchQuery, 0);
			} else if (config.showInitialResults === true) {
				// Perform an empty search to show initial results
				performSearch("", 0);
			}

			// Only fetch and show facets if enableFacets is true
			if (
				config.enableFacets &&
				(bruidAlgolia.contentType === "aanbieder" ||
					bruidAlgolia.contentType === "post")
			) {
				fetchAllSchemaSections()
					.then((sections) => {
						allSchemaSections = sections;
						// Render filter tags
						renderFilterTags(allSchemaSections);
					})
					.catch((error) => {
						// Error fetching schema sections
					});
			}
		} catch (error) {
			hitsContainer.innerHTML = `
				<div class="flex flex-col justify-center items-center px-4 py-12">
					<h3 class="mb-4 text-xl font-semibold text-red-600">Error</h3>
					<p class="text-center text-gray-600">Could not initialize Algolia search: ${error.message}</p>
				</div>
			`;
		}
	}, 500); // Half-second delay to ensure everything is loaded

	// Function to disable any existing autocomplete instances
	function disableExistingAutocomplete() {
		try {
			// Get the search input element
			const searchInput = document.getElementById("algolia-search-input");

			// Remove any existing autocomplete event listeners
			if (searchInput) {
				// Clone the input to remove all event listeners
				const newInput = searchInput.cloneNode(true);
				searchInput.parentNode.replaceChild(newInput, searchInput);

				// Re-add the search input ID
				newInput.id = "algolia-search-input";
			}

			// Hide any existing autocomplete dropdowns
			const existingDropdowns = document.querySelectorAll(
				".algolia-autocomplete, .algolia-autocomplete-dropdown, .algolia-autocomplete-menu",
			);
			existingDropdowns.forEach((dropdown) => {
				dropdown.style.display = "none";
			});

			// Remove any autocomplete-related classes from the search input
			if (searchInput) {
				searchInput.classList.remove(
					"algolia-autocomplete-input",
					"autocomplete-input",
				);
			}
		} catch (error) {
			// Error disabling existing autocomplete
		}
	}

	// Function to initialize wp-search-with-algolia autocomplete
	function initializeAutocomplete() {
		try {
			// Hide the custom results containers since we're using autocomplete
			if (hitsContainer) hitsContainer.style.display = "none";
			if (paginationContainer) paginationContainer.style.display = "none";
			if (filtersContainer) filtersContainer.style.display = "none";

			// Check if wp-search-with-algolia autocomplete is available
			if (typeof window.AlgoliaAutocomplete !== "undefined") {
				// Initialize the autocomplete using wp-search-with-algolia
				const autocomplete = new window.AlgoliaAutocomplete({
					input: searchInput,
					index: bruidAlgolia.exactIndex,
					appId: bruidAlgolia.appId,
					searchKey: bruidAlgolia.searchKey,
					// Use plugin settings for autocomplete configuration
					debounce: bruidAlgolia.pluginSettings?.autocompleteDebounce || 200,
					// Configure the autocomplete behavior
					onSelect: function (item) {
						// Handle item selection - redirect to the item URL
						if (item.url) {
							window.location.href = item.url;
						} else if (item.permalink) {
							window.location.href = item.permalink;
						}
					},
					// Customize the autocomplete dropdown styling
					dropdownClass: "algolia-autocomplete-dropdown",
					// Show loading state
					showLoadingIndicator: true,
					// Respect plugin's powered by setting
					showPoweredBy: bruidAlgolia.pluginSettings?.showPoweredBy || false,
					// Configure search parameters
					searchParameters: {
						hitsPerPage: 10,
						attributesToRetrieve: [
							"objectID",
							"post_title",
							"post_excerpt",
							"permalink",
							"featured_image",
							"name",
							"url",
						],
						attributesToHighlight: ["post_title", "post_excerpt", "name"],
						highlightPreTag: "<mark>",
						highlightPostTag: "</mark>",
					},
					// Add post type filter if specified
					...(config.postTypeFilter &&
						bruidAlgolia.contentType === "post" && {
						filters:
							config.postTypeFilter === "custom"
								? "NOT type:post AND NOT type:page"
								: `type:${config.postTypeFilter}`,
					}),
				});
			} else {
				// Fallback: show error message if autocomplete is not available
				searchInput.placeholder =
					"Autocomplete not available - please check plugin configuration";
				searchInput.disabled = true;
			}
		} catch (error) {
			// Fallback to disabled input
			searchInput.placeholder = "Error initializing autocomplete";
			searchInput.disabled = true;
		}
	}
});
